import { useState } from 'react'
import { Badge, Button } from '../../../shared/ui'
import { toast } from '../../../lib/toast'
import {
  connectTool,
  disconnectTool,
  syncTool,
  TOOL_ORDER,
  TOOLS,
  toolName,
  type ToolConnection,
  type ToolProvider,
} from '../../../lib/outreachConnectors'

/**
 * The sending tools of one business: what is connected, and a paste-a-key
 * form per tool. Owners and admins manage; members see status only.
 */

function timeAgo(iso: string | null): string {
  if (!iso) return 'not yet'
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const h = Math.round(mins / 60)
  if (h < 24) return `${h} h ago`
  return `${Math.round(h / 24)} d ago`
}

function statusBadge(c: ToolConnection) {
  switch (c.status) {
    case 'syncing':
      return <Badge tone="warn">Syncing</Badge>
    case 'error':
      return <Badge tone="err">Sync problem</Badge>
    case 'needs_reconnect':
      return <Badge tone="err">New key needed</Badge>
    default:
      return <Badge tone="ok">Connected</Badge>
  }
}

function errMsg(e: unknown): string {
  return e instanceof Error ? e.message : 'Something went wrong.'
}

function KeyForm({
  provider,
  workspaceId,
  onDone,
  onCancel,
}: {
  provider: ToolProvider
  workspaceId: string
  onDone: () => void
  onCancel?: (() => void) | undefined
}) {
  const [key, setKey] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const name = TOOLS[provider].label
  const id = `ot-key-${provider}`

  const submit = async () => {
    const k = key.trim()
    if (!k || busy) return
    setBusy(true)
    setError('')
    try {
      await connectTool(provider, k, workspaceId)
      setKey('')
      toast(`${name} connected. Pulling in your outreach now.`, 'success')
      onDone()
    } catch (e) {
      setError(errMsg(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form
      className="ot-block"
      onSubmit={(e) => {
        e.preventDefault()
        void submit()
      }}
    >
      <label className="ot-dim" htmlFor={id}>
        {TOOLS[provider].keyHint}
      </label>
      <div className="ot-key">
        <input
          id={id}
          className="settings-input"
          type="password"
          autoComplete="off"
          spellCheck={false}
          placeholder={`Paste ${name} API key`}
          aria-label={`${name} API key`}
          value={key}
          onChange={(e) => setKey(e.target.value)}
          disabled={busy}
        />
        <Button type="submit" disabled={busy || !key.trim()}>
          {busy ? 'Checking...' : 'Connect'}
        </Button>
        {onCancel && (
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
        )}
      </div>
      {error && (
        <div className="ot-error" role="alert">
          {error}
        </div>
      )}
    </form>
  )
}

function ConnectionRow({
  c,
  manage,
  onChanged,
}: {
  c: ToolConnection
  manage: boolean
  onChanged: () => void
}) {
  const [busy, setBusy] = useState(false)
  const [rekey, setRekey] = useState(false)
  const name = toolName(c)

  const run = async (fn: () => Promise<void>) => {
    if (busy) return
    setBusy(true)
    try {
      await fn()
    } catch (e) {
      toast(errMsg(e), 'error')
    } finally {
      setBusy(false)
    }
  }

  const sync = () =>
    run(async () => {
      const added = await syncTool(c)
      toast(`${name}: ${added} new ${added === 1 ? 'activity' : 'activities'}`, 'success')
      onChanged()
    })
  const disconnect = () =>
    run(async () => {
      if (
        !window.confirm(
          `Disconnect ${name}? Hunts stops syncing it. Activity already synced stays on your contacts.`,
        )
      )
        return
      await disconnectTool(c)
      toast(`${name} disconnected`, 'success')
      onChanged()
    })

  return (
    <li className="ot-box" aria-label={name}>
      <div className="ot-row">
        <span>
          <span className="ot-name">{name}</span>
          {name !== TOOLS[c.provider].label && (
            <span className="ot-dim"> · {TOOLS[c.provider].label}</span>
          )}
        </span>
        {statusBadge(c)}
      </div>
      <div className="ot-dim">
        Last sync {timeAgo(c.last_sync_at)} · {c.activities.toLocaleString()} activities ·{' '}
        {c.contacts.toLocaleString()} contacts
      </div>
      {c.last_sync_error && c.status !== 'connected' && c.status !== 'syncing' && (
        <div className="ot-error" role="alert">
          {c.last_sync_error}
        </div>
      )}
      {manage && rekey && (
        <KeyForm
          provider={c.provider}
          workspaceId={c.workspace_id}
          onDone={() => {
            setRekey(false)
            onChanged()
          }}
          onCancel={() => setRekey(false)}
        />
      )}
      {manage && !rekey && (
        <div className="ot-actions">
          {c.needs_reconnect ? (
            <Button onClick={() => setRekey(true)} aria-label={`Paste a new key for ${name}`}>
              Paste new key
            </Button>
          ) : (
            <Button
              variant="secondary"
              onClick={() => void sync()}
              disabled={busy}
              aria-label={`Sync ${name} now`}
            >
              {busy ? 'Syncing...' : 'Sync now'}
            </Button>
          )}
          <Button
            variant="danger"
            onClick={() => void disconnect()}
            disabled={busy}
            aria-label={`Disconnect ${name}`}
          >
            Disconnect
          </Button>
        </div>
      )}
    </li>
  )
}

export function ToolsSection({
  workspaceId,
  canManage,
  connections,
  onChanged,
}: {
  workspaceId: string
  canManage: boolean
  connections: ToolConnection[]
  onChanged: () => void
}) {
  const [adding, setAdding] = useState<ToolProvider | null>(null)

  return (
    <ul className="ot-list" aria-label="Sending tools">
      {TOOL_ORDER.map((p) => {
        const mine = connections.filter((c) => c.provider === p)
        const showForm = canManage && (mine.length === 0 || adding === p)
        return (
          <li key={p} className="ot-card" aria-label={TOOLS[p].label}>
            <div className="ot-row">
              <span className="ot-name">{TOOLS[p].label}</span>
              {mine.length === 0 && <Badge tone="muted">Not connected</Badge>}
            </div>
            <div className="ot-dim">{TOOLS[p].what}</div>
            {mine.length > 0 && (
              <ul className="ot-list">
                {mine.map((c) => (
                  <ConnectionRow key={c.id} c={c} manage={canManage} onChanged={onChanged} />
                ))}
              </ul>
            )}
            {showForm && (
              <KeyForm
                provider={p}
                workspaceId={workspaceId}
                onDone={() => {
                  setAdding(null)
                  onChanged()
                }}
                onCancel={mine.length > 0 ? () => setAdding(null) : undefined}
              />
            )}
            {canManage && mine.length > 0 && adding !== p && (
              <div className="ot-actions">
                <Button variant="secondary" onClick={() => setAdding(p)}>
                  Add another {TOOLS[p].label} account
                </Button>
              </div>
            )}
            {!canManage && mine.length === 0 && (
              <div className="ot-dim">Ask an owner or admin of this business to connect it.</div>
            )}
          </li>
        )
      })}
    </ul>
  )
}
